import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useHistory, useParams } from 'react-router-dom';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import Button from '../../components/Button';
import ScrollToTopButton from '../../components/ScrollToTopButton';
import SingleSelect from '../../components/SingleSelect';
import FileUpload from '../../components/FileUpload';
import GlobalModal from '../../components/GlobalModal';
import ArchiveModal from './ArchiveModal';
import ReInstateModal from './ReInstateModal';
import LoadingSpinner from '../../components/LoadingSpinner';
import DisplayDate from '../../components/DisplayDate';
import DateInput from '../../components/DateInput';

// TYPES
import { RouteParams } from '../../Interfaces';
import { PolicyEditCommand } from '../../api/models';

const PolicySubmit: React.FC = () => {
	const history = useHistory();

	// Check if Organisation is active
	const isOrganisationActive = useAppSelector((state) => state.user.isOrganisationActive);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Get Id from params
	const params = useParams<RouteParams>();
	const policyId = params.id;

	// Get all Policy Access Types from Redux Store
	const policyAccessTypes = useAppSelector((state) => state.lists.policyAccessTypes);

	// State to control Modals
	const [showArchiveModal, setShowArchiveModal] = React.useState<boolean>(false);
	const [showReInstateModal, setShowReInstateModal] = React.useState<boolean>(false);

	// Set up data state and initial data
	const initialPoliciesDetailData: PolicyEditCommand = {
		id: undefined,
		title: undefined,
		status: undefined,
		expiryDate: undefined,
		leadershipTeamAccess: undefined,
		staffAccess: undefined,
		siteVisitorAccess: undefined,
		parentAccess: undefined,
		volunteerAccess: undefined,
		childrenAccess: undefined,
		fileName: undefined,
		isDocumentUpdated: undefined,
		displayFileName: undefined,
		attachmentUrl: undefined,
		versions: [],
		updated: undefined
	};

	const [policiesFormData, setPoliciesFormData] = React.useState<PolicyEditCommand>(initialPoliciesDetailData);

	const getPoliciesDetailsData = async () => {
		if (policyId) {
			setIsLoading(true);

			const response = await apibridge.getPoliciesDetailsData(policyId);

			if (response.isError) {
				setIsLoading(false);

				setFormErrorMessage(
					response.validationErrors.length
						? validationErrorsToString(response.validationErrors)
						: response.message || 'An error has occurred'
				);
			} else {
				const { result = initialPoliciesDetailData } = response;

				setIsLoading(false);

				setPoliciesFormData(result);
			}
		} else {
			setIsLoading(false);

			setPoliciesFormData(initialPoliciesDetailData);
		}
	};

	React.useEffect(() => {
		getPoliciesDetailsData();
	}, []);

	const {
		id,
		title,
		expiryDate,
		leadershipTeamAccess,
		staffAccess,
		siteVisitorAccess,
		volunteerAccess,
		parentAccess,
		childrenAccess,
		versions,
		status,
		displayFileName,
		attachmentUrl,
		fileName,
		updated
	} = policiesFormData;

	const submitPolicy = async (policiesFormData: PolicyEditCommand) => {
		if (policiesFormData !== undefined) {
			setIsLoading(true);

			const response = await apibridge.submitPolicy(policiesFormData);

			if (response.isError) {
				setIsLoading(false);

				setFormErrorMessage(
					response.validationErrors.length
						? validationErrorsToString(response.validationErrors)
						: response.message || 'An error has occurred'
				);
			} else {
				setIsLoading(false);

				history.push('/policies');
			}
		}
	};

	const onFormSubmit = (values: any) => {
		const data: PolicyEditCommand = {
			...policiesFormData,

			title: values.titleInput,
			expiryDate: values.expiryDateInput,
			leadershipTeamAccess: values.leadershipTeamAccessInput,
			staffAccess: values.staffAccessInput,
			siteVisitorAccess: values.siteVisitorAccessInput,
			parentAccess: values.parentAccessInput,
			childrenAccess: values.childrenAccessInput,
			volunteerAccess: values.parentVolunteerAccessInput,

			fileName: values.fileNameInput !== policiesFormData.displayFileName ? values.fileNameInput : '',
			displayFileName: '',
			attachmentUrl: ''
		};

		submitPolicy(data);
	};

	const validationSchema = Yup.object({
		titleInput: Yup.string().required('Required'),
		expiryDateInput: Yup.string().required('Required'),
		fileNameInput: Yup.string().required('Required'),
		leadershipTeamAccessInput: Yup.string().required('Required'),
		staffAccessInput: Yup.string().required('Required'),
		siteVisitorAccessInput: Yup.string().required('Required'),
		parentAccessInput: Yup.string().required('Required'),
		childrenAccessInput: Yup.string().required('Required'),
		parentVolunteerAccessInput: Yup.string().required('Required')
	});

	const initialValues = {
		titleInput: title || '',
		expiryDateInput: expiryDate || '',
		fileNameInput: fileName || '',
		leadershipTeamAccessInput: leadershipTeamAccess || '',
		staffAccessInput: staffAccess || '',
		siteVisitorAccessInput: siteVisitorAccess || '',
		parentAccessInput: parentAccess || '',
		childrenAccessInput: childrenAccess || '',
		parentVolunteerAccessInput: volunteerAccess || ''
	};

	return (
		<div className="page-general-layout">
			<div className="general-divider"></div>

			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								{status === 'Archived' && (
									<>
										<h2 className="archived-notification">This policy is archived</h2>

										<p className="sub-grey-text mbtom-20">
											you will need to re-instate it to make it available to users again.
										</p>
									</>
								)}

								{updated && (
									<p className="archived-text">
										Updated <DisplayDate>{updated}</DisplayDate>
									</p>
								)}

								<Formik
									initialValues={initialValues}
									validationSchema={validationSchema}
									onSubmit={onFormSubmit}
									enableReinitialize
								>
									{(formik) => {
										return (
											<Form translate="yes" className="general-form">
												<div className="general-form-layout-wrapper direction-normal-column">
													<div
														className={`form-main ${status === 'Archived' || !isOrganisationActive ? 'archived' : ''}`}
													>
														<div className="form-group">
															<label htmlFor="titleInput" className="label">
																Policy title
															</label>

															<div className="form-field">
																<Field name="titleInput" id="titleInput" type="text" className="form-input" />
																<ErrorMessage name="titleInput" component="div" className="error-message" />
															</div>
														</div>

														<div className="form-group">
															<label htmlFor="additionalCommentInput" className="label">
																Policy document{' '}
																{versions && versions.length ? `(Version ${versions[0].versionNumber})` : '(Version 1)'}
															</label>

															<div className="form-field mbtom-8">
																<FileUpload
																	name="fileNameInput"
																	maxSizeMb={5}
																	multiple={false}
																	onChange={(vals: string) => formik.setFieldValue('fileNameInput', vals)}
																	files={
																		displayFileName
																			? [{ id: displayFileName, displayFileName: displayFileName, link: attachmentUrl }]
																			: []
																	}
																/>
															</div>

															<ErrorMessage name="fileNameInput" component="div" className="error-message" />
														</div>

														<div className="form-group">
															<label className="label">Expiry date</label>

															<DateInput
																dateTimeString={formik.values.expiryDateInput || ''}
																onChange={(val: string) => formik.setFieldValue('expiryDateInput', val)}
																excludePast={true}
															/>

															<ErrorMessage name="expiryDateInput" component="div" className="error-message" />
														</div>

														<h2 className="form-section-title">Policy access</h2>

														<div className="general-divider mbtom-30"></div>

														<div className="form-group">
															<label htmlFor="leadershipTeamAccessInput" className="label">
																Leadership team
															</label>

															<SingleSelect
																isDisabled={status === 'Archived' || !isOrganisationActive}
																options={policyAccessTypes}
																name="leadershipTeamAccessInput"
																value={formik.values.leadershipTeamAccessInput}
																onChange={(val: string) => formik.setFieldValue('leadershipTeamAccessInput', val)}
															/>

															<ErrorMessage
																name="leadershipTeamAccessInput"
																component="div"
																className="error-message"
															/>
														</div>

														<div className="form-group">
															<label htmlFor="staffAccessInput" className="label">
																Staff or Volunteer
															</label>

															<SingleSelect
																isDisabled={status === 'Archived' || !isOrganisationActive}
																options={policyAccessTypes}
																name="staffAccessInput"
																value={formik.values.staffAccessInput}
																onChange={(val: string) => formik.setFieldValue('staffAccessInput', val)}
															/>
															<ErrorMessage name="staffAccessInput" component="div" className="error-message" />
														</div>

														<div className="form-group">
															<label htmlFor="siteVisitorAccessInput" className="label">
																Visitors
															</label>

															<SingleSelect
																isDisabled={status === 'Archived' || !isOrganisationActive}
																options={policyAccessTypes}
																name="siteVisitorAccessInput"
																value={formik.values.siteVisitorAccessInput}
																onChange={(val: string) => formik.setFieldValue('siteVisitorAccessInput', val)}
															/>
															<ErrorMessage name="siteVisitorAccessInput" component="div" className="error-message" />
														</div>

														<div className="form-group">
															<label htmlFor="parentVolunteerAccessInput" className="label">
																Parent Volunteers
															</label>

															<SingleSelect
																isDisabled={status === 'Archived' || !isOrganisationActive}
																options={policyAccessTypes}
																name="parentVolunteerAccessInput"
																value={formik.values.parentVolunteerAccessInput}
																onChange={(val: string) => formik.setFieldValue('parentVolunteerAccessInput', val)}
															/>
															<ErrorMessage
																name="parentVolunteerAccessInput"
																component="div"
																className="error-message"
															/>
														</div>

														<div className="form-group">
															<label htmlFor="parentAccessInput" className="label">
																Parents
															</label>

															<SingleSelect
																isDisabled={status === 'Archived' || !isOrganisationActive}
																options={policyAccessTypes}
																name="parentAccessInput"
																value={formik.values.parentAccessInput}
																onChange={(val: string) => formik.setFieldValue('parentAccessInput', val)}
															/>
															<ErrorMessage name="parentAccessInput" component="div" className="error-message" />
														</div>

														<div className="form-group">
															<label htmlFor="childrenAccessInput" className="label">
																Child or Student
															</label>

															<SingleSelect
																isDisabled={status === 'Archived' || !isOrganisationActive}
																options={policyAccessTypes}
																name="childrenAccessInput"
																value={formik.values.childrenAccessInput}
																onChange={(val: string) => formik.setFieldValue('childrenAccessInput', val)}
															/>
															<ErrorMessage name="childrenAccessInput" component="div" className="error-message" />
														</div>
													</div>

													{versions && versions.length > 0 && (
														<div className="general-info-feed-container">
															<div className="feed-header">Version history</div>

															{versions.map((item, index) => {
																return (
																	<div key={index} className="feed-item">
																		<h2 className="item-title">Version {item.versionNumber}</h2>

																		<div className="item-detail">
																			<p className="date">
																				Added &nbsp;
																				<DisplayDate>{item.created}</DisplayDate>
																			</p>

																			<a className="btn btn-text pdt-0" download href={item.attachmentUrl}>
																				Download
																			</a>
																		</div>
																	</div>
																);
															})}
														</div>
													)}
												</div>

												<div className="general-divider"></div>

												{isOrganisationActive && (
													<div className="form-button-group">
														{status === 'Archived' ? (
															<Button
																className="btn-primary submit-btn complaints-submit-page"
																onClick={() => setShowReInstateModal(true)}
															>
																Re-instate policy
															</Button>
														) : (
															<Button type="submit" className="btn-primary submit-btn complaints-submit-page">
																Save
															</Button>
														)}
													</div>
												)}
											</Form>
										);
									}}
								</Formik>

								{isOrganisationActive && status === 'Active' && (
									<section className="general-guide">
										<h2 className="guide-header grey-line">Need to archive this policy completely?</h2>

										<p className="guide-info">
											You can archive this policy to make it unavailable to all users, however it will be kept on file
											for future reference.
										</p>

										<Button className="btn btn-primary guide-btn" onClick={() => setShowArchiveModal(true)}>
											Archive policy
										</Button>
									</section>
								)}

								<ScrollToTopButton extraClass="mtop-40" />

								<GlobalModal isOpen={showArchiveModal} setIsOpen={setShowArchiveModal} titleText="Archive policy">
									<ArchiveModal title={title} policyId={policyId} />
								</GlobalModal>

								<GlobalModal
									isOpen={showReInstateModal}
									setIsOpen={setShowReInstateModal}
									titleText="Re-instate policy"
								>
									<ReInstateModal title={title} policyId={policyId} />
								</GlobalModal>
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default PolicySubmit;
